import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography, Pagination,
} from '@mui/material';
import { Add as AddIcon, Visibility } from '@mui/icons-material';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { PurchaseOrder, PurchaseOrderStatus } from '../../interfaces/procurement/purchaseOrder.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { formatFc, isForeignCurrency } from '../../utils/fcDocuments'; // G5a

type FilterOption = PurchaseOrderStatus | undefined;

interface StatusChip {
  label: string;
  value: FilterOption;
}

const STATUS_CHIPS: StatusChip[] = [
  { label: 'All', value: undefined },
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Pending Approval', value: 'PENDING_APPROVAL' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Ordered', value: 'ORDERED' },
  { label: 'Partially Received', value: 'PARTIALLY_RECEIVED' },
  { label: 'Received', value: 'RECEIVED' },
  { label: 'Closed', value: 'CLOSED' },
  { label: 'Rejected', value: 'REJECTED' },
  { label: 'Cancelled', value: 'CANCELLED' },
];

const STATUS_COLORS: Record<PurchaseOrderStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'info',
  ORDERED: 'primary',
  PARTIALLY_RECEIVED: 'secondary',
  RECEIVED: 'success',
  CLOSED: 'default',
  REJECTED: 'error',
  CANCELLED: 'error',
};

export const PurchaseOrderList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.PURCHASE_ORDER_MANAGE); // create is PURCHASE_ORDER_MANAGE server-side
  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [statusFilter, setStatusFilter] = useState<FilterOption>(undefined);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

  const fetchPOs = async (page: number = 0, filter: FilterOption = statusFilter) => {
    try {
      setLoading(true);
      const response = await PurchaseOrderService.listByInstitution(page, 10, filter);
      setPos(response.content);
      setPageInfo({ currentPage: response.number, totalPages: response.totalPages, totalElements: response.totalElements });
    } catch (e) {
      toast.showApiError(e, 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPOs(0, statusFilter);
  }, [statusFilter]);

  const handleChipClick = (value: FilterOption) => {
    setStatusFilter(value);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    fetchPOs(page - 1, statusFilter);
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.poList.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Purchase Orders</Typography>
        {canManage && (
          <Button data-help="procurement.poList.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/procurement/purchase-orders/new')}>
            New Purchase Order
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }} data-help="procurement.poList.statusFilter">
        {STATUS_CHIPS.map(chip => (
          <Chip
            key={chip.label}
            label={chip.label}
            clickable
            variant={statusFilter === chip.value ? 'filled' : 'outlined'}
            color={statusFilter === chip.value ? 'primary' : 'default'}
            onClick={() => handleChipClick(chip.value)}
          />
        ))}
      </Box>

      <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper} data-help-section="procurement.poList.table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-help="procurement.poList.col.number">PO Number</TableCell>
                <TableCell data-help="procurement.poList.col.supplier">Supplier</TableCell>
                <TableCell data-help="procurement.poList.col.date">Order Date</TableCell>
                <TableCell data-help="procurement.poList.col.status">Status</TableCell>
                <TableCell data-help="procurement.poList.col.total">Total</TableCell>
                <TableCell data-help="procurement.poList.col.actions">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pos.map(po => (
                <TableRow key={po.id}>
                  <TableCell>{po.poNumber}</TableCell>
                  <TableCell title={po.supplierId}>{po.supplierName ?? po.supplierId}</TableCell>
                  <TableCell>{formatDate(po.orderDate)}</TableCell>
                  <TableCell>
                    <Chip
                      data-help="procurement.poList.status"
                      label={humanizeEnum(po.status)}
                      color={STATUS_COLORS[po.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatMoney(po.totalAmount)}
                    {isForeignCurrency(po.currencyCode) && po.currencyCode && (
                      <Typography variant="caption" display="block" color="text.secondary">{formatFc(po.currencyCode, po.foreignTotal)}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View" arrow>
                      <IconButton
                        data-help="procurement.poList.action.view"
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/procurement/purchase-orders/${po.id}`)}
                        aria-label="View"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pageInfo.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageInfo.totalPages}
            page={pageInfo.currentPage + 1}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default PurchaseOrderList;
